import { supabase } from './client';

// --- Tournament CRUD ---

export async function getTournaments() {
  const { data, error } = await supabase
    .from('tournaments')
    .select('*')
    .order('start_date', { ascending: false });

  if (error) throw error;
  return data.map(t => ({
    id: t.id,
    name: t.name,
    startDate: t.start_date,
    endDate: t.end_date,
    venue: t.venue,
    leagueWinningPoint: t.league_winning_point,
    leagueGoldenPoint: t.league_golden_point,
    playoffWinningPoint: t.playoff_winning_point,
    playoffGoldenPoint: t.playoff_golden_point,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
  }));
}

export async function getTournament(id: string) {
  const { data, error } = await supabase
    .from('tournaments')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return {
    id: data.id,
    name: data.name,
    startDate: data.start_date,
    endDate: data.end_date,
    venue: data.venue,
    leagueWinningPoint: data.league_winning_point,
    leagueGoldenPoint: data.league_golden_point,
    playoffWinningPoint: data.playoff_winning_point,
    playoffGoldenPoint: data.playoff_golden_point,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export async function createTournament(data: {
  name: string;
  startDate: string;
  endDate: string;
  venue?: string;
  leagueWinningPoint: number;
  leagueGoldenPoint: number;
  playoffWinningPoint: number;
  playoffGoldenPoint: number;
  teamIds?: string[];
}) {
  const { data: result, error } = await supabase
    .from('tournaments')
    .insert({
      name: data.name,
      start_date: data.startDate,
      end_date: data.endDate,
      venue: data.venue || null,
      league_winning_point: data.leagueWinningPoint,
      league_golden_point: data.leagueGoldenPoint,
      playoff_winning_point: data.playoffWinningPoint,
      playoff_golden_point: data.playoffGoldenPoint,
    })
    .select()
    .single();

  if (error) throw error;

  // Add participating teams if provided
  if (data.teamIds && data.teamIds.length > 0) {
    for (const teamId of data.teamIds) {
      await supabase
        .from('tournament_teams')
        .insert({ tournament_id: result.id, team_id: teamId });
    }
  }

  return {
    id: result.id,
    name: result.name,
    startDate: result.start_date,
    endDate: result.end_date,
    venue: result.venue,
    leagueWinningPoint: result.league_winning_point,
    leagueGoldenPoint: result.league_golden_point,
    playoffWinningPoint: result.playoff_winning_point,
    playoffGoldenPoint: result.playoff_golden_point,
    createdAt: result.created_at,
    updatedAt: result.updated_at,
  };
}

export async function updateTournament(id: string, data: {
  name?: string;
  startDate?: string;
  endDate?: string;
  venue?: string;
  leagueWinningPoint?: number;
  leagueGoldenPoint?: number;
  playoffWinningPoint?: number;
  playoffGoldenPoint?: number;
  teamIds?: string[];
}) {
  const update: Record<string, unknown> = {};
  if (data.name !== undefined) update.name = data.name;
  if (data.startDate !== undefined) update.start_date = data.startDate;
  if (data.endDate !== undefined) update.end_date = data.endDate;
  if (data.venue !== undefined) update.venue = data.venue || null;
  if (data.leagueWinningPoint !== undefined) update.league_winning_point = data.leagueWinningPoint;
  if (data.leagueGoldenPoint !== undefined) update.league_golden_point = data.leagueGoldenPoint;
  if (data.playoffWinningPoint !== undefined) update.playoff_winning_point = data.playoffWinningPoint;
  if (data.playoffGoldenPoint !== undefined) update.playoff_golden_point = data.playoffGoldenPoint;

  const { data: result, error } = await supabase
    .from('tournaments')
    .update(update)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  // Update participating teams if provided
  if (data.teamIds !== undefined) {
    await setTournamentTeams(id, data.teamIds);
  }

  return {
    id: result.id,
    name: result.name,
    startDate: result.start_date,
    endDate: result.end_date,
    venue: result.venue,
    leagueWinningPoint: result.league_winning_point,
    leagueGoldenPoint: result.league_golden_point,
    playoffWinningPoint: result.playoff_winning_point,
    playoffGoldenPoint: result.playoff_golden_point,
    createdAt: result.created_at,
    updatedAt: result.updated_at,
  };
}

export async function deleteTournament(id: string) {
  console.log('[DELETE TOURNAMENT] Starting delete for tournament ID:', id);

  // Delete the tournament directly - CASCADE will handle tournament_teams automatically
  // Do NOT manually delete tournament_teams first as that triggers the minimum teams constraint
  console.log('[DELETE TOURNAMENT] Deleting tournament record (CASCADE will handle related records)...');
  const { error, count } = await supabase.from('tournaments').delete().eq('id', id);
  if (error) {
    console.error('[DELETE TOURNAMENT] Supabase error:', error);
    throw new Error(`Database error: ${error.message}`);
  }

  console.log('[DELETE TOURNAMENT] Delete completed. Affected rows:', count);
}

// --- Tournament Participating Teams ---

export async function getParticipatingTeams(tournamentId: string) {
  const { data, error } = await supabase
    .from('tournament_teams')
    .select(`
      team_id,
      teams (
        id,
        name,
        short_name,
        color,
        logo_url,
        players (
          id,
          name,
          number,
          team_id
        )
      )
    `)
    .eq('tournament_id', tournamentId);

  if (error) throw error;
  // Format to match Team type with camelCase properties
  return (data ?? []).map((tt: any) => {
    const t = tt.teams;
    return {
      id: t.id,
      name: t.name,
      shortName: t.short_name,
      color: t.color,
      logoUrl: t.logo_url,
      players: (t.players || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        number: p.number,
        teamId: p.team_id,
      })),
    };
  });
}

export async function addTeamToTournament(tournamentId: string, teamId: string) {
  const { error } = await supabase
    .from('tournament_teams')
    .insert({ tournament_id: tournamentId, team_id: teamId });
  if (error) throw error;
}

export async function removeTeamFromTournament(tournamentId: string, teamId: string) {
  const { error } = await supabase
    .from('tournament_teams')
    .delete()
    .eq('tournament_id', tournamentId)
    .eq('team_id', teamId);
  if (error) throw error;
}

export async function setTournamentTeams(tournamentId: string, teamIds: string[]) {
  // Get current teams
  const { data: current, error: fetchErr } = await supabase
    .from('tournament_teams')
    .select('team_id')
    .eq('tournament_id', tournamentId);
  if (fetchErr) throw fetchErr;

  const currentIds = new Set((current ?? []).map((t: any) => t.team_id));
  const newIds = new Set(teamIds);

  // Teams to add
  const toAdd = teamIds.filter(id => !currentIds.has(id));
  // Teams to remove
  const toRemove = [...currentIds].filter(id => !newIds.has(id));

  // Add new teams
  for (const teamId of toAdd) {
    await addTeamToTournament(tournamentId, teamId);
  }

  // Remove old teams (this will validate teams not in matches)
  for (const teamId of toRemove) {
    await removeTeamFromTournament(tournamentId, teamId);
  }
}

export async function getTournamentStats(tournamentId: string) {
  const { data: matches, error } = await supabase
    .from('matches')
    .select('id, status')
    .eq('tournament_id', tournamentId)
    .neq('status', 'discarded');

  if (error) throw error;

  return {
    total: matches.length,
    completed: matches.filter(m => m.status === 'match-won').length,
    live: matches.filter(m => m.status === 'live' || m.status === 'set-won').length,
    pending: matches.filter(m => m.status === 'setup' || m.status === 'first-server' || m.status === 'scheduled').length,
  };
}
